import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { getChopColor } from '../utils/colors';
import { computePeaks, rangeMinMax, sizeCanvas, fillEnvelopeShape } from '../utils/waveform';
import Wordmark from './Wordmark';
import SamplerOptions from './SamplerOptions';
import './ChopStation.css';

const LANE_HEIGHT = 110;
const LANE_GAP = 18;
const LANE_MAX_SCALE = 220; // px per second cap so tiny chops don't blow up
const MAX_ZOOM = 200;
const ACCENT = '#FF4141';
const IDLE_WAVE = '#DCDCDC';
const DIM_ALPHA = 0.35;

// Peaks are expensive; cache per AudioBuffer so every wave/cell shares them.
const peaksCache = new WeakMap();
function getPeaks(buffer) {
  if (!peaksCache.has(buffer)) peaksCache.set(buffer, computePeaks(buffer));
  return peaksCache.get(buffer);
}

// Playback seeker, clipped to the waveform: a black line whose top and
// bottom follow the envelope's height at its position, so it rides inside
// the shape instead of overflowing it. tFrac0/tFrac1 is the ~1px slice of
// the full file at the seeker.
function drawSeeker(ctx, peaks, tFrac0, tFrac1, x, cssH) {
  const [mn, mx] = rangeMinMax(peaks, tFrac0, tFrac1);
  const mid = cssH / 2;
  const amp = (cssH - 6) / 2;
  let yT = mid - mx * amp;
  let yB = mid - mn * amp;
  if (yB - yT < 6) { yT = mid - 3; yB = mid + 3; }
  ctx.fillStyle = '#111111';
  ctx.fillRect(x - 1, yT, 2, yB - yT);
}

// Split the visible window into color runs (idle gray / per-chop color).
function buildSegments(chops, flashing, active, isPlaying, time, t0, t1) {
  const segs = [];
  let cursor = t0;
  for (const c of chops) {
    const end = c.isOpen && isPlaying ? Math.max(time, c.start) : c.end;
    const s = Math.max(c.start, cursor, t0);
    const e = Math.min(end, t1);
    if (e <= s) continue;
    if (s > cursor) segs.push({ s: cursor, e: s, color: IDLE_WAVE });
    const lit = (c.isOpen && isPlaying) || flashing?.has(c.key) || active?.has(c.key);
    segs.push({ s, e, color: lit ? c.color.hex : `rgba(${c.color.rgb}, ${DIM_ALPHA})` });
    cursor = e;
  }
  if (cursor < t1) segs.push({ s: cursor, e: t1, color: IDLE_WAVE });
  return segs;
}

// One source's waveform for its visible window [t0, t0 + duration/zoom].
function drawWave(canvas, peaks, duration, cssH, state, time, viewStart, zoom, override, playback) {
  const cssW = canvas.clientWidth;
  if (!cssW || !duration) return;
  const ctx = sizeCanvas(canvas, cssW, cssH);
  ctx.clearRect(0, 0, cssW, cssH);

  const { chops, flashing, active, isPlaying, selectedChopId } = state;
  const t0 = viewStart;
  const t1 = viewStart + duration / zoom;
  const pxPerSec = cssW / (t1 - t0);

  let effChops = chops;
  if (override && chops.some((c) => c.id === override.id)) {
    effChops = chops
      .map((c) => (c.id === override.id ? { ...c, start: override.start, end: override.end, isOpen: false } : c))
      .sort((a, b) => a.start - b.start);
  }

  for (const seg of buildSegments(effChops, flashing, active, isPlaying, time, t0, t1)) {
    const xA = (seg.s - t0) * pxPerSec;
    const xB = (seg.e - t0) * pxPerSec;
    fillEnvelopeShape(ctx, peaks, duration, seg.s, seg.e, xA, xB, cssH, seg.color);
  }

  // Edge handles for the selected chop (click a chop in the lane to select)
  const sel = effChops.find((c) => c.id === selectedChopId);
  if (sel) {
    const selEnd = sel.isOpen && isPlaying ? Math.max(time, sel.start) : sel.end;
    for (const t of [sel.start, selEnd]) {
      if (t < t0 || t > t1) continue;
      const x = (t - t0) * pxPerSec;
      ctx.fillStyle = '#111111';
      ctx.fillRect(x - 1, 0, 2, cssH);
      ctx.beginPath();
      ctx.arc(x, cssH / 2, 8, 0, Math.PI * 2);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = '#111111';
      ctx.stroke();
    }
  }

  // Glowing seeker through each chop of this source that's sounding
  if (playback) {
    const now = performance.now();
    for (const c of effChops) {
      const entry = active?.has(c.key) ? playback[c.key] : null;
      if (!entry) continue;
      const elapsed = ((now - entry.startedAt) / 1000) * entry.rate;
      const tSeek = Math.min(c.end, c.start + elapsed);
      if (tSeek < t0 || tSeek > t1) continue;
      drawSeeker(ctx, peaks, tSeek / duration, (tSeek + 1 / pxPerSec) / duration, (tSeek - t0) * pxPerSec, cssH);
    }
  }

  if (time >= t0 && time <= t1) {
    ctx.fillStyle = ACCENT;
    ctx.fillRect(Math.min(cssW - 2, (time - t0) * pxPerSec), 0, 2, cssH);
  }
}

// One chop's slice at full color; the cell's CSS opacity handles the dimmed
// resting state. Returns the css width so callers can overlay the seeker.
function drawSegment(canvas, peaks, duration, t0, t1, color, scale) {
  if (!canvas || !duration || t1 <= t0 || !scale) return 0;
  const cssW = Math.max(20, Math.round((t1 - t0) * scale));
  const cssH = LANE_HEIGHT;
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  const ctx = sizeCanvas(canvas, cssW, cssH);
  ctx.clearRect(0, 0, cssW, cssH);
  fillEnvelopeShape(ctx, peaks, duration, t0, t1, 0, cssW, cssH, color.hex);
  return cssW;
}

function formatTime(seconds) {
  const s = Math.max(0, seconds || 0);
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`;
}

const ChopCell = ({ chop, peaks, duration, scale, sourcePlaying, timeRef, flashing, active, selected, playbackRef, onClick, onContextMenu, dlStartedAt = null, dlStart = null, dlEnd = null }) => {
  const canvasRef = useRef(null);
  const growing = chop.isOpen && sourcePlaying;
  const looping = dlStartedAt != null; // this chop IS the running drum loop
  const lit = flashing || active || growing || looping;

  useEffect(() => {
    let raf;
    const draw = () => {
      const end = growing ? Math.max(timeRef.current, chop.start + 0.02) : chop.end;
      const w = drawSegment(canvasRef.current, peaks, duration, chop.start, end, chop.color, scale);

      // Seeker sweeps the cell while this chop is sounding
      const entry = playbackRef?.current?.[chop.key];
      if (active && entry && w) {
        const elapsed = (performance.now() - entry.startedAt) / 1000;
        const frac = Math.max(0, Math.min(1, (elapsed * entry.rate) / (chop.end - chop.start)));
        const ctx = canvasRef.current.getContext('2d');
        const tSeek = chop.start + frac * (end - chop.start);
        const slice = (end - chop.start) / Math.max(1, w);
        drawSeeker(ctx, peaks, tSeek / duration, (tSeek + slice) / duration, Math.min(w - 2, frac * w), LANE_HEIGHT);
      }

      // Loop seeker cycles the cell while this chop is the backing drum loop
      if (looping && w) {
        const loopLen = Math.max(0.05, dlEnd - dlStart);
        const pos = dlStart + (((performance.now() - dlStartedAt) / 1000) % loopLen);
        const frac = Math.max(0, Math.min(1, (pos - chop.start) / (end - chop.start)));
        const ctx = canvasRef.current.getContext('2d');
        const slice = (end - chop.start) / Math.max(1, w);
        drawSeeker(ctx, peaks, pos / duration, (pos + slice) / duration, Math.min(w - 2, frac * w), LANE_HEIGHT);
      }

      if (growing || active || looping) raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [growing, active, looping, dlStartedAt, dlStart, dlEnd, chop.start, chop.end, chop.color, chop.key, peaks, duration, scale, timeRef, playbackRef]);

  return (
    <div
      className={`chop-cell${lit ? ' lit' : ''}${selected ? ' selected' : ''}`}
      onClick={onClick}
      onContextMenu={onContextMenu}
      title={`${chop.key} · ${(chop.end - chop.start).toFixed(2)}s`}
    >
      <canvas ref={canvasRef} />
      <span className="chop-cell-key" style={{ color: chop.color.hex }}>
        {chop.key}
      </span>
    </div>
  );
};

/**
 * SourceWave — one source's waveform strip: canvas, pinch zoom/pan, seek,
 * selected-chop edge dragging, glowing seekers. Clicking an unfocused wave
 * focuses its source instead of seeking.
 */
const SourceWave = ({
  source,
  height,
  focused,
  isPlaying,          // true only when THIS source is the focused, playing one
  timeRef,
  getMedia,
  isSegActive,
  onCommitTime,       // (t) => remember resume position for this source
  onFocus,
  visualChops,
  flashingChops,
  activeChops,
  chopPlaybackRef,
  selectedChopId,
  setSelectedChopId,
  chopOverrideRef,
  onUpdateChopStart,
  onUpdateChopEnd,
  showTime
}) => {
  const canvasRef = useRef(null);
  const timeElRef = useRef(null);
  const zoomRef = useRef(1);
  const viewStartRef = useRef(0);
  const chopDragRef = useRef(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const duration = source.audioBuffer.duration;
  const peaks = getPeaks(source.audioBuffer);

  const drawStateRef = useRef({});
  drawStateRef.current = {
    chops: visualChops,
    flashing: flashingChops,
    active: activeChops,
    isPlaying,
    selectedChopId
  };

  const clampView = useCallback(() => {
    const maxStart = Math.max(0, duration - duration / zoomRef.current);
    viewStartRef.current = Math.max(0, Math.min(maxStart, viewStartRef.current));
  }, [duration]);

  // Pinch (ctrl/cmd + wheel) zooms toward the cursor; horizontal wheel pans.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const oldZoom = zoomRef.current;
      const oldDur = duration / oldZoom;
      if (e.ctrlKey || e.metaKey) {
        const newZoom = Math.min(MAX_ZOOM, Math.max(1, oldZoom * Math.exp(-e.deltaY * 0.012)));
        const anchor = viewStartRef.current + frac * oldDur;
        zoomRef.current = newZoom;
        viewStartRef.current = anchor - frac * (duration / newZoom);
        setIsZoomed(newZoom > 1.001);
      } else if (oldZoom > 1) {
        viewStartRef.current += e.deltaX * (oldDur / rect.width);
      }
      clampView();
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [duration, clampView]);

  const xToTime = (clientX) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.max(0, Math.min(duration, viewStartRef.current + frac * (duration / zoomRef.current)));
  };

  const chopEdgeAt = (clientX) => {
    if (selectedChopId == null) return null;
    const chop = visualChops.find((c) => c.id === selectedChopId);
    if (!chop) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    const viewDur = duration / zoomRef.current;
    const toX = (t) => rect.left + ((t - viewStartRef.current) / viewDur) * rect.width;
    const dStart = Math.abs(clientX - toX(chop.start));
    const dEnd = Math.abs(clientX - toX(chop.end));
    if (dStart <= 14 && dStart <= dEnd) return 'start';
    if (dEnd <= 14) return 'end';
    return null;
  };

  const handlePointerDown = (e) => {
    if (!duration) return;
    if (!focused) {
      onFocus();
      return;
    }
    const edge = chopEdgeAt(e.clientX);
    if (edge) {
      e.currentTarget.setPointerCapture(e.pointerId);
      const chop = visualChops.find((c) => c.id === selectedChopId);
      chopDragRef.current = edge;
      chopOverrideRef.current = { id: chop.id, start: chop.start, end: chop.end };
      return;
    }
    // Plain click: seek this source, deselect any selected chop
    setSelectedChopId(null);
    const t = xToTime(e.clientX);
    timeRef.current = t;
    onCommitTime(t);
    const m = getMedia();
    if (m && !isSegActive()) m.currentTime = t;
  };

  const handlePointerMove = (e) => {
    if (!duration || !focused) return;
    if (!chopDragRef.current) {
      canvasRef.current.style.cursor = chopEdgeAt(e.clientX) ? 'ew-resize' : 'crosshair';
      return;
    }
    // Re-seed if a mid-drag render cleared the override
    let o = chopOverrideRef.current;
    if (!o) {
      const chop = visualChops.find((c) => c.id === selectedChopId);
      if (!chop) {
        chopDragRef.current = null;
        return;
      }
      o = chopOverrideRef.current = { id: chop.id, start: chop.start, end: chop.end };
    }
    const t = xToTime(e.clientX);
    if (chopDragRef.current === 'start') o.start = Math.min(t, o.end - 0.05);
    else o.end = Math.max(t, o.start + 0.05);
  };

  const handlePointerUp = () => {
    if (!chopDragRef.current) return;
    const edge = chopDragRef.current;
    const o = chopOverrideRef.current;
    chopDragRef.current = null;
    if (!o) return;
    if (edge === 'start') onUpdateChopStart(o.id, o.start);
    else onUpdateChopEnd(o.id, o.end);
  };

  // Draw loop: track the focused source's playhead, auto-follow when zoomed,
  // repaint, and keep the time readout current without re-rendering React.
  useEffect(() => {
    let raf;
    const tick = () => {
      const m = getMedia();
      if (focused && m && !m.paused && !isSegActive()) {
        timeRef.current = m.currentTime;
        onCommitTime(m.currentTime);
        const viewDur = duration / zoomRef.current;
        if (zoomRef.current > 1 &&
            (timeRef.current < viewStartRef.current ||
             timeRef.current > viewStartRef.current + viewDur * 0.97)) {
          viewStartRef.current = timeRef.current - viewDur * 0.1;
          clampView();
        }
      }
      const canvas = canvasRef.current;
      if (canvas) {
        drawWave(
          canvas, peaks, duration, height,
          drawStateRef.current, timeRef.current,
          viewStartRef.current, zoomRef.current,
          chopOverrideRef.current, chopPlaybackRef?.current
        );
      }
      if (timeElRef.current) {
        timeElRef.current.textContent = `${formatTime(timeRef.current)} / ${formatTime(duration)}`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [peaks, duration, height, focused, clampView, getMedia, isSegActive, onCommitTime, timeRef, chopOverrideRef, chopPlaybackRef]);

  return (
    <div className={`source-wave${focused ? '' : ' unfocused'}${isZoomed ? ' zoomed' : ''}`}>
      <canvas
        ref={canvasRef}
        className="station-wave-canvas"
        style={{ height }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />
      {showTime && <div className="station-time" ref={timeElRef} />}
    </div>
  );
};

/** Inline "add audio file" flow: upload a file or paste a YouTube link. */
const AddSourcePanel = ({ onFile, onUrl }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState(null);
  const inputRef = useRef(null);

  const submitUrl = async (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    setStatus('sampling…');
    try {
      await onUrl(trimmed);
      setOpen(false);
      setStatus(null);
      setUrl('');
    } catch (e) {
      setStatus(e.message || 'could not sample that video');
    }
  };

  const submitFile = async (file) => {
    setStatus('loading…');
    try {
      await onFile(file);
      setOpen(false);
      setStatus(null);
    } catch (e) {
      setStatus(e.message || 'could not load that file');
    }
  };

  return (
    <div className="add-source">
      <button
        type="button"
        className="add-source-toggle"
        onClick={(e) => { setOpen(!open); setStatus(null); e.currentTarget.blur(); }}
      >
        add audio file
      </button>
      {open && (
        <div className="add-source-panel">
          <button type="button" className="add-source-upload" onClick={() => inputRef.current?.click()}>
            upload a file
          </button>
          <input
            className="add-source-url"
            type="text"
            value={url}
            placeholder="or paste a youtube link"
            spellCheck={false}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') submitUrl(url); }}
            onPaste={(e) => {
              const text = e.clipboardData.getData('text');
              if (text) { e.preventDefault(); setUrl(text); submitUrl(text); }
            }}
          />
          {status && <div className="add-source-status">{status}</div>}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) submitFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
};

/**
 * ChopStation — the loaded state, now multi-source. Videos tile in a row,
 * waveforms stack below; one source is focused at a time (click to focus)
 * and receives transport + new chops, while the shared keyboard triggers
 * chops from every source. Chops collect in one lane, grouped by source.
 */
const ChopStation = ({
  sources,
  focusedId,
  onFocusSource,
  onAddSourceFile,
  onAddSourceUrl,
  flashingChops,
  activeChops,
  chopPlaybackRef,
  isPlaying,
  setIsPlaying,
  transportRef,
  videoVisualizerRef,
  onChopClick,
  onUpdateChopStart,
  onUpdateChopEnd,
  onRemoveChop,
  onLoopChop,
  drumLoopVisual,
  onSourcePause,
  samplerOptions,
  transpose,
  drumLoop
}) => {
  const mediaRefs = useRef({});       // id -> media element
  const timeRefs = useRef({});        // id -> { current } playhead seconds
  const sourceTimeRefs = useRef({});  // id -> resume position
  const segRefs = useRef({});         // id -> { active, timer, seq }
  const audioUrlsRef = useRef({});    // id -> { file, url } for audio-only sources
  const chopOverrideRef = useRef(null);

  const [selectedChopId, setSelectedChopId] = useState(null);
  const [laneWidth, setLaneWidth] = useState(0);
  const [chopMenu, setChopMenu] = useState(null); // { chop, x, y }

  // Close the chop context menu on any outside press or Escape
  useEffect(() => {
    if (!chopMenu) return;
    const close = () => setChopMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [chopMenu]);

  const focusedSource = sources.find((s) => s.id === focusedId) || sources[0];
  const waveHeight = sources.length > 1 ? 150 : 200;

  // Source playback follows the focused layer's transpose (rate-based
  // repitch, like the chops — so pitch preservation is disabled).
  const transposeRate = Math.pow(2, (transpose?.value || 0) / 12);
  const applyRate = (m, rate) => {
    if (!m) return;
    try {
      m.preservesPitch = false;
      m.webkitPreservesPitch = false;
    } catch { /* older browsers */ }
    m.playbackRate = rate;
  };

  const timeRefOf = (id) => {
    if (!timeRefs.current[id]) timeRefs.current[id] = { current: 0 };
    return timeRefs.current[id];
  };
  const segOf = (id) => {
    if (!segRefs.current[id]) segRefs.current[id] = { active: false, timer: null, seq: 0 };
    return segRefs.current[id];
  };

  // Object URLs for audio-only sources (videos stream from the backend)
  const audioUrlOf = (source) => {
    const map = audioUrlsRef.current;
    const entry = map[source.id];
    if (!entry || entry.file !== source.audioFile) {
      if (entry) URL.revokeObjectURL(entry.url);
      map[source.id] = { file: source.audioFile, url: URL.createObjectURL(source.audioFile) };
    }
    return map[source.id].url;
  };
  useEffect(() => {
    const alive = new Set(sources.filter((s) => !s.videoUrl).map((s) => s.id));
    for (const key of Object.keys(audioUrlsRef.current)) {
      if (!alive.has(Number(key))) {
        URL.revokeObjectURL(audioUrlsRef.current[key].url);
        delete audioUrlsRef.current[key];
      }
    }
  }, [sources]);

  const visualChopsOf = useMemo(() => {
    const map = {};
    for (const s of sources) {
      map[s.id] = s.chops.map((c) => ({ ...c, color: getChopColor(c.key) }));
    }
    return map;
  }, [sources]);

  useEffect(() => {
    const measure = () => setLaneWidth(Math.min(1040, window.innerWidth * 0.92));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // One lane scale across every source's finalized chops.
  const laneScale = useMemo(() => {
    const all = sources.flatMap((s) => s.chops);
    if (!laneWidth || all.length === 0) return LANE_MAX_SCALE / 4;
    const closed = all.filter((c) => !c.isOpen);
    if (closed.length === 0) return LANE_MAX_SCALE / 4;
    const gaps = LANE_GAP * Math.max(0, all.length - 1);
    const total = closed.reduce((s, c) => s + Math.max(0.05, c.end - c.start), 0);
    return Math.max(8, Math.min(LANE_MAX_SCALE, (laneWidth - gaps) / total));
  }, [sources, laneWidth]);

  // Drop the drag override once chop DATA actually changes (a commit), not
  // merely when the arrays get new identity on a re-render — mid-drag
  // re-renders (flashes, autosave, drum loop) must not null the override.
  const allChops = useMemo(() => sources.flatMap((s) => s.chops), [sources]);
  const chopsSignature = useMemo(
    () => allChops.map((c) => `${c.id}:${c.start}:${c.end ?? ''}`).join('|'),
    [allChops]
  );
  useEffect(() => {
    chopOverrideRef.current = null;
  }, [chopsSignature]);
  useEffect(() => {
    if (selectedChopId != null && !allChops.some((c) => c.id === selectedChopId)) {
      setSelectedChopId(null);
    }
  }, [allChops, selectedChopId]);

  // Escape deselects; delete/backspace removes the click-selected chop.
  useEffect(() => {
    if (selectedChopId == null) return;
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key === 'Escape') {
        setSelectedChopId(null);
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        onRemoveChop(selectedChopId);
        setSelectedChopId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedChopId, onRemoveChop]);

  // --- Focus + transport ---------------------------------------------------
  const pauseAll = useCallback(() => {
    for (const s of sources) {
      const m = mediaRefs.current[s.id];
      if (!m) continue;
      if (!m.paused) {
        // Mark as segment-style pause so the async pause handler doesn't
        // double-run source bookkeeping (we do it synchronously below).
        const seg = segOf(s.id);
        if (s.id === focusedId && !seg.active) {
          const t = m.currentTime;
          sourceTimeRefs.current[s.id] = t;
          timeRefOf(s.id).current = t;
          onSourcePause(t);
          seg.active = true; // consumed by the pause handler
        }
        m.pause();
      }
      if (s.videoUrl) m.muted = true;
    }
    setIsPlaying(false);
  }, [sources, focusedId, onSourcePause, setIsPlaying]);

  const focusSource = useCallback((id) => {
    if (id === focusedId) return;
    pauseAll();
    setSelectedChopId(null);
    onFocusSource(id);
  }, [focusedId, pauseAll, onFocusSource]);

  const togglePlayFocused = useCallback(() => {
    const s = focusedSource;
    if (!s) return;
    const m = mediaRefs.current[s.id];
    if (!m) return;
    const seg = segOf(s.id);
    if (seg.active) {
      clearTimeout(seg.timer);
      seg.active = false;
      m.pause();
    }
    if (m.paused) {
      applyRate(m, transposeRate);
      if (s.videoUrl) m.muted = false;
      m.currentTime = sourceTimeRefs.current[s.id] || 0;
      const p = m.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      m.pause();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedSource, transposeRate]);

  // Transpose changes apply live to a playing source
  useEffect(() => {
    const m = mediaRefs.current[focusedId];
    if (m && !m.paused && !segOf(focusedId).active) applyRate(m, transposeRate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transposeRate, focusedId]);

  useEffect(() => {
    transportRef.current = {
      getCurrentTime: () => mediaRefs.current[focusedId]?.currentTime || 0,
      playPause: togglePlayFocused
    };
    return () => { transportRef.current = null; };
  }, [transportRef, focusedId, togglePlayFocused]);

  // Per-chop video scrubbing, addressed by owning source id.
  useEffect(() => {
    if (!videoVisualizerRef) return;
    videoVisualizerRef.current = {
      playSegment: (layerId, start, segDuration, playbackRate = 1) => {
        const m = mediaRefs.current[layerId];
        if (!m) return null;
        const seg = segOf(layerId);
        // Never hijack this media mid source-playback.
        if (!m.paused && !seg.active) return null;
        clearTimeout(seg.timer);
        seg.active = true;
        seg.seq += 1;
        m.muted = true;
        // The video scrubs at the chop's rate so it stays in sync with the
        // transposed audio (covering the same source span in the same time).
        applyRate(m, Math.max(0.0625, Math.min(16, playbackRate || 1)));
        try {
          m.currentTime = Math.max(0, start || 0);
          const p = m.play();
          if (p && p.catch) p.catch(() => {});
        } catch { /* metadata not ready */ }
        const wallClock = Math.max(0.05, (segDuration || 0.4) / (playbackRate || 1));
        seg.timer = setTimeout(() => {
          if (mediaRefs.current[layerId] && seg.active) mediaRefs.current[layerId].pause();
        }, wallClock * 1000);
        return seg.seq;
      },
      stopSegment: (layerId, seq) => {
        const seg = segRefs.current[layerId];
        if (!seg || seq == null || seq !== seg.seq || !seg.active) return;
        clearTimeout(seg.timer);
        mediaRefs.current[layerId]?.pause();
      }
    };
    return () => { videoVisualizerRef.current = null; };
  }, [videoVisualizerRef]);

  const handleMediaPlay = (id) => {
    if (segOf(id).active) return;
    if (id !== focusedId) return;
    setIsPlaying(true);
  };

  const handleMediaPause = (id, hasVideo) => {
    const seg = segOf(id);
    if (seg.active) {
      seg.active = false;
      return;
    }
    if (id !== focusedId) return;
    const m = mediaRefs.current[id];
    const t = m ? m.currentTime : 0;
    sourceTimeRefs.current[id] = t;
    timeRefOf(id).current = t;
    if (hasVideo && m) m.muted = true;
    setIsPlaying(false);
    onSourcePause(t);
  };

  const handleMediaEnded = (id) => {
    sourceTimeRefs.current[id] = 0;
  };

  const handleVideoClick = (id) => {
    if (id !== focusedId) focusSource(id);
    else togglePlayFocused();
  };

  const videoSources = sources.filter((s) => s.videoUrl);
  const audioSources = sources.filter((s) => !s.videoUrl);

  const hint = allChops.length === 0
    ? 'press space to play · tap letter keys while it plays to chop'
    : isPlaying
      ? 'tap letter keys to set more chops · space to stop'
      : 'tap your keys to play chops · space to keep chopping';

  return (
    <div className="station">
      <div className="station-brand"><Wordmark /></div>
      {focusedSource?.title && (
        <div className="station-file" title={focusedSource.title}>{focusedSource.title}</div>
      )}

      <AddSourcePanel onFile={onAddSourceFile} onUrl={onAddSourceUrl} />
      {samplerOptions && <SamplerOptions options={samplerOptions} transpose={transpose} drumLoop={drumLoop} />}

      {videoSources.length > 0 && (
        <div className={`station-videos${videoSources.length > 1 ? ' multi' : ''}`}>
          {videoSources.map((s) => (
            <video
              key={s.id}
              ref={(el) => { if (el) mediaRefs.current[s.id] = el; }}
              className={`station-video${s.id === focusedId ? '' : ' unfocused'}`}
              src={s.videoUrl}
              playsInline
              preload="auto"
              muted
              onClick={() => handleVideoClick(s.id)}
              onPlay={() => handleMediaPlay(s.id)}
              onPause={() => handleMediaPause(s.id, true)}
              onEnded={() => handleMediaEnded(s.id)}
            />
          ))}
        </div>
      )}

      {audioSources.map((s) => (
        <audio
          key={s.id}
          ref={(el) => { if (el) mediaRefs.current[s.id] = el; }}
          src={audioUrlOf(s)}
          preload="auto"
          onPlay={() => handleMediaPlay(s.id)}
          onPause={() => handleMediaPause(s.id, false)}
          onEnded={() => handleMediaEnded(s.id)}
        />
      ))}

      <div className="station-waves">
        {sources.map((s) => (
          <SourceWave
            key={s.id}
            source={s}
            height={waveHeight}
            focused={s.id === focusedId}
            isPlaying={s.id === focusedId && isPlaying}
            timeRef={timeRefOf(s.id)}
            getMedia={() => mediaRefs.current[s.id]}
            isSegActive={() => segOf(s.id).active}
            onCommitTime={(t) => { sourceTimeRefs.current[s.id] = t; }}
            onFocus={() => focusSource(s.id)}
            visualChops={visualChopsOf[s.id] || []}
            flashingChops={flashingChops}
            activeChops={activeChops}
            chopPlaybackRef={chopPlaybackRef}
            selectedChopId={selectedChopId}
            setSelectedChopId={setSelectedChopId}
            chopOverrideRef={chopOverrideRef}
            onUpdateChopStart={onUpdateChopStart}
            onUpdateChopEnd={onUpdateChopEnd}
            showTime={s.id === focusedId}
          />
        ))}
      </div>

      {allChops.length > 0 && (
        <div className="chop-lane">
          {sources.flatMap((s) =>
            (visualChopsOf[s.id] || []).map((c) => (
              <ChopCell
                key={`${s.id}-${c.id}`}
                chop={c}
                peaks={getPeaks(s.audioBuffer)}
                duration={s.audioBuffer.duration}
                scale={laneScale}
                sourcePlaying={s.id === focusedId && isPlaying}
                timeRef={timeRefOf(s.id)}
                flashing={flashingChops?.has(c.key)}
                active={activeChops?.has(c.key)}
                selected={c.id === selectedChopId}
                playbackRef={chopPlaybackRef}
                dlStartedAt={drumLoopVisual?.chopId === c.id ? drumLoopVisual.startedAt : null}
                dlStart={drumLoopVisual?.chopId === c.id ? drumLoopVisual.offsetStart : null}
                dlEnd={drumLoopVisual?.chopId === c.id ? drumLoopVisual.offsetEnd : null}
                onClick={() => {
                  onChopClick(c);
                  setSelectedChopId((prev) => (prev === c.id ? null : c.id));
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setChopMenu({
                    chop: c,
                    x: Math.min(e.clientX, window.innerWidth - 150),
                    y: Math.min(e.clientY, window.innerHeight - 110)
                  });
                }}
              />
            ))
          )}
        </div>
      )}

      {chopMenu && (
        <div
          className="chop-menu"
          style={{ left: chopMenu.x, top: chopMenu.y }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="chop-menu-item"
            onClick={() => { onLoopChop(chopMenu.chop); setChopMenu(null); }}
            title="loop this chop as the backing drums"
          >
            loop
          </button>
          <button
            type="button"
            className="chop-menu-item danger"
            onClick={() => { onRemoveChop(chopMenu.chop.id); setChopMenu(null); }}
          >
            delete
          </button>
        </div>
      )}

      <div className="station-hint">{hint}</div>
    </div>
  );
};

export default ChopStation;
